import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, UserCircle, Search, ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateBR } from '../utils/dateUtils';

const ITEMS_PER_PAGE = 50;

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc');
  const { isAdmin, user } = useAuth();

  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1 };
  const roleLevel = ROLE_LEVELS[user?.role] || 0;
  const canFilterByUnit = roleLevel === ROLE_LEVELS.marcador_geral || roleLevel >= ROLE_LEVELS.admin_geral;

  const [formData, setFormData] = useState({
    name: '',
    sus_card: '',
    cpf: '',
    birth_date: '',
    address: '',
    phone: '',
    unit_id: ''
  });

  useEffect(() => {
    // Carregar unidades uma vez
    api.get('/health-units').then(res => setUnits(res.data)).catch(() => {});
  }, []);

  // Debounce na busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Buscar dados quando filtros mudam
  const prevFiltersRef = React.useRef('');
  useEffect(() => {
    const key = `${searchQuery}-${filterUnit}-${sortOrder}`;
    if (prevFiltersRef.current && prevFiltersRef.current !== key) {
      setCurrentPage(1);
      fetchPaginated(1);
    } else {
      fetchPaginated(currentPage);
    }
    prevFiltersRef.current = key;
  }, [currentPage, searchQuery, filterUnit, sortOrder]);

  const fetchPaginated = async (pg) => {
    try {
      let params = new URLSearchParams();
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (filterUnit) params.append('unit_id', filterUnit);
      params.append('page', pg || currentPage);
      params.append('per_page', ITEMS_PER_PAGE);
      params.append('sort_order', sortOrder);
      
      const res = await api.get(`/patients/paginated?${params.toString()}`);
      setPatients(res.data.data);
      setTotalPatients(res.data.total);
    } catch (error) {
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.unit_id) {
      toast.error('Por favor, selecione uma unidade de saúde');
      return;
    }

    const cpfDigits = (formData.cpf || '').replace(/\D/g, '');
    if (cpfDigits.length !== 11) {
      toast.error('O CPF é obrigatório e deve conter 11 dígitos');
      return;
    }
    
    try {
      if (editingPatient) {
        await api.put(`/patients/${editingPatient.id}`, formData);
        toast.success('Paciente atualizado com sucesso!');
      } else {
        await api.post('/patients', formData);
        toast.success('Paciente cadastrado com sucesso!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao salvar paciente';
      toast.error(errorMessage);
    }
  };

  // Recarrega a página atual (chamada auxiliar após criar/editar/deletar)
  const fetchData = () => fetchPaginated(currentPage);

  const formatCPF = (value) => {
    // Remove tudo que não é dígito
    const cpf = value.replace(/\D/g, '');
    
    // Aplica a máscara
    if (cpf.length <= 11) {
      return cpf
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return cpf.slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setFormData({ ...formData, cpf: formatted });
  };

  const formatSUSCard = (value) => {
    // Remove tudo que não é dígito
    const sus = value.replace(/\D/g, '');
    
    // Aplica a máscara para o Cartão SUS (15 dígitos)
    // Formato: 000 0000 0000 0000
    if (sus.length <= 15) {
      return sus
        .replace(/(\d{3})(\d)/, '$1 $2')
        .replace(/(\d{4})(\d)/, '$1 $2')
        .replace(/(\d{4})(\d)/, '$1 $2');
    }
    return sus.slice(0, 15)
      .replace(/(\d{3})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2');
  };

  const handleSUSCardChange = (e) => {
    const formatted = formatSUSCard(e.target.value);
    setFormData({ ...formData, sus_card: formatted });
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name,
      sus_card: patient.sus_card,
      cpf: patient.cpf || '',
      birth_date: patient.birth_date,
      address: patient.address,
      phone: patient.phone,
      unit_id: patient.unit_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (patientId) => {
    if (!window.confirm('Tem certeza que deseja excluir este paciente?')) return;
    
    try {
      await api.delete(`/patients/${patientId}`);
      toast.success('Paciente excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir paciente');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sus_card: '',
      cpf: '',
      birth_date: '',
      address: '',
      phone: '',
      unit_id: ''
    });
    setEditingPatient(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando pacientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="patients-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Pacientes</h1>
          <p className="text-slate-600">Gerencie os pacientes cadastrados no sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-10 px-6 font-medium shadow-sm"
              onClick={() => resetForm()}
              data-testid="add-patient-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {editingPatient ? 'Editar Paciente' : 'Novo Paciente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-8">
                  <Label htmlFor="name">Nome Completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    placeholder="NOME COMPLETO DO PACIENTE"
                    required
                    className="h-11 rounded-lg uppercase"
                    data-testid="patient-name-input"
                  />
                </div>

                <div className="md:col-span-4">
                  <Label htmlFor="birth_date">Data de Nascimento *</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    required
                    className="h-11 rounded-lg"
                  />
                </div>

                <div className="md:col-span-6">
                  <Label htmlFor="sus_card">Cartão SUS *</Label>
                  <Input
                    id="sus_card"
                    value={formData.sus_card}
                    onChange={handleSUSCardChange}
                    placeholder="000 0000 0000 0000"
                    required
                    maxLength={18}
                    className="h-11 rounded-lg"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Identificador único - 15 dígitos
                  </p>
                </div>

                <div className="md:col-span-6">
                  <Label htmlFor="cpf">CPF *</Label>
                  <Input
                    id="cpf"
                    value={formData.cpf}
                    onChange={handleCPFChange}
                    placeholder="000.000.000-00"
                    maxLength={14}
                    required
                    className="h-11 rounded-lg"
                    data-testid="patient-cpf-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Campo obrigatório e único - não pode haver dois pacientes com o mesmo CPF
                  </p>
                </div>

                <div className="md:col-span-12">
                  <Label htmlFor="address">Endereço Completo *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value.toUpperCase() })}
                    placeholder="RUA, NÚMERO, BAIRRO, CIDADE"
                    required
                    className="h-11 rounded-lg uppercase"
                    data-testid="patient-address-input"
                  />
                </div>

                <div className="md:col-span-6">
                  <Label htmlFor="phone">Telefone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    required
                    className="h-11 rounded-lg"
                  />
                </div>

                <div className="md:col-span-6">
                  <Label htmlFor="unit">Unidade de Saúde *</Label>
                  <Select 
                    value={formData.unit_id} 
                    onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                  >
                    <SelectTrigger className="h-11 rounded-lg" data-testid="patient-unit-select">
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(unit => (
                        <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Unidade de saúde onde o paciente é cadastrado (obrigatório)
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="save-patient-button"
                >
                  {editingPatient ? 'Atualizar' : 'Cadastrar'} Paciente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar + Filtro Unidade + Ordenação */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[250px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Buscar por nome, cartão SUS ou CPF..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-lg"
            data-testid="search-patient-input"
          />
        </div>
        {canFilterByUnit && units.length > 1 && (
          <Select value={filterUnit || 'all'} onValueChange={(value) => setFilterUnit(value === 'all' ? '' : value)}>
            <SelectTrigger className="h-11 w-[250px] rounded-lg" data-testid="filter-unit-patients">
              <SelectValue placeholder="Todas as unidades" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as unidades</SelectItem>
              {units.map(unit => (
                <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          className="h-11 px-4 rounded-lg text-slate-600 hover:text-teal-700"
          data-testid="sort-patients-button"
        >
          <ArrowUpDown className="h-4 w-4 mr-2" />
          {sortOrder === 'desc' ? 'Mais recentes' : 'Mais antigos'}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Info de paginação acima da tabela */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-600" data-testid="patients-count-info">
            {totalPatients} paciente(s) encontrado(s)
            {totalPatients > ITEMS_PER_PAGE && (
              <> — Página {currentPage} de {Math.ceil(totalPatients / ITEMS_PER_PAGE)}</>
            )}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold text-xs h-12">
              <tr>
                <th className="px-6 py-3">Paciente</th>
                <th className="px-6 py-3">Cartão SUS</th>
                <th className="px-6 py-3">Data Nasc.</th>
                <th className="px-6 py-3">Unidade</th>
                <th className="px-6 py-3">Telefone</th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {patients.length > 0 ? (
                patients
                  .map((patient) => (
                  <tr key={patient.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors h-14">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-sky-50 flex items-center justify-center">
                          <UserCircle className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{patient.name}</p>
                          <p className="text-xs text-slate-500">{patient.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-slate-600 font-mono text-xs">{patient.sus_card}</td>
                    <td className="px-6 py-3 text-slate-600">{formatDateBR(patient.birth_date)}</td>
                    <td className="px-6 py-3 text-slate-600">
                      {patient.unit_id ? (
                        units.find(u => u.id === patient.unit_id)?.name || 'Não encontrada'
                      ) : (
                        <span className="text-slate-400 italic">Não informada</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{patient.phone}</td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(patient)}
                          className="h-8"
                          data-testid={`edit-patient-${patient.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {isAdmin() && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(patient.id)}
                            className="h-8 text-rose-600 hover:bg-rose-50 hover:border-rose-200"
                            data-testid={`delete-patient-${patient.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    {searchTerm ? 'Nenhum paciente encontrado' : 'Nenhum paciente cadastrado'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Controles de Paginação */}
        {totalPatients > ITEMS_PER_PAGE && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between" data-testid="patients-pagination">
            <span className="text-sm text-slate-500">
              Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, totalPatients)} de {totalPatients}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-9"
                data-testid="patients-prev-page"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              {Array.from({ length: Math.ceil(totalPatients / ITEMS_PER_PAGE) }, (_, i) => i + 1)
                .filter(page => {
                  const totalPages = Math.ceil(totalPatients / ITEMS_PER_PAGE);
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="px-2 text-slate-400">...</span>
                  ) : (
                    <Button
                      key={item}
                      variant={currentPage === item ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(item)}
                      className={`h-9 w-9 p-0 ${currentPage === item ? 'bg-teal-700 hover:bg-teal-800' : ''}`}
                      data-testid={`patients-page-${item}`}
                    >
                      {item}
                    </Button>
                  )
                )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalPatients / ITEMS_PER_PAGE), p + 1))}
                disabled={currentPage >= Math.ceil(totalPatients / ITEMS_PER_PAGE)}
                className="h-9"
                data-testid="patients-next-page"
              >
                Próxima
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
